import React, { useState } from "react";
import { toast } from "react-toastify";
import { formatDateTimeAmORPm } from "../../utils/common";

interface UserDetailsModalProps {
  open: boolean;
  user: any;
  onClose: () => void;
  loading?: boolean;
  title?: string;
}

const tabs = [
  "Personal Info",
  "Organization Info",
  "Connection Preferences",
  "Platform Activity",
];

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  open,
  user,
  onClose,
  loading,
  title = "User Details",
}) => {
  const [activeTab, setActiveTab] = useState("Personal Info");

  if (!open) return null;

  // 🔥 Show Loader if fetching or user is empty
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center gap-4 w-[300px]">
          <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-gray-600 text-sm font-medium">
            {`Loading ${title}...`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="w-[75vw] bg-white rounded-xl shadow-lg p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold mb-4">{title}</h2>

          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-200 transition text-red-600 hover:text-red-900"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b mb-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                activeTab === tab
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ---------------- TAB CONTENTS ---------------- */}

        <div className="text-sm text-gray-800 space-y-4">
          {/* 🔵 Personal Info */}
          {activeTab === "Personal Info" && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
              <h3 className="text-base font-semibold text-blue-700 border-l-4 border-blue-500 pl-2">
                Personal Information
              </h3>

              <div className="grid grid-cols-3 gap-4 items-start">
                {/* Text Info */}
                <div className="col-span-2 grid grid-cols-2 gap-3">
                  <p>
                    <strong>Name:</strong> {user?.first_name} {user?.last_name}
                  </p>
                  <p>
                    <strong>Email:</strong> {user?.email}
                  </p>
                  <p>
                    <strong>Phone:</strong> {user?.phone_no}
                  </p>
                  <p>
                    <strong>Gender:</strong> {user?.gender}
                  </p>
                  <p>
                    <strong>DOB:</strong> {user?.dob}
                  </p>
                  <p>
                    <strong>City:</strong> {user?.city}
                  </p>
                  <p>
                    <strong>Country:</strong> {user?.country}
                  </p>
                  <p>
                    <strong>Height:</strong> {user?.height || "N/A"}
                  </p>
                  <p>
                    <strong>Occupation:</strong>{" "}
                    {user?.job_title || user?.workplace
                      ? `${user?.job_title || ""}${
                          user?.job_title && user?.workplace ? " at " : ""
                        }${user?.workplace || ""}`
                      : "N/A"}
                  </p>
                </div>

                {/* Profile Image */}
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={user?.profile_photo || "/default-avatar.png"}
                    alt="Profile"
                    className="w-32 h-32 rounded-xl object-cover shadow-md border border-gray-300"
                  />

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(user?.profile_photo || "");
                      toast.success("Profile image URL copied!");
                    }}
                    className="text-xs text-blue-600 underline hover:text-blue-800"
                  >
                    Copy URL
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="mt-3 space-y-2">
                <h4 className="text-sm font-semibold text-blue-700">Status</h4>

                <div className="flex items-center gap-3 bg-white border border-blue-200 p-3 rounded-lg">
                  <span
                    className={`w-3 h-3 rounded-full ${
                      user?.blocked ? "bg-red-500" : "bg-green-500"
                    }`}
                  />
                  <span
                    className={`text-xs font-medium px-3 py-1 rounded-md ${
                      user?.blocked
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {user?.blocked ? "Blocked User" : "Active User"}
                  </span>
                </div>

                <div className="bg-white border border-blue-200 p-3 rounded-lg">
                  <p>
                    <strong>Profile Status:</strong>{" "}
                    {user?.profile_status || "Incomplete"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 🟢 Organization */}
          {activeTab === "Organization Info" && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
              <h3 className="text-base font-semibold text-green-700 border-l-4 border-green-500 pl-2">
                Organization Details
              </h3>
              <p>
                <strong>Org Name:</strong> {user?.host_alias || "N/A"}
              </p>
            </div>
          )}

          {/* 🟡 Connection Preferences */}
          {activeTab === "Connection Preferences" && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl space-y-2">
              <h3 className="text-base font-semibold text-yellow-700 border-l-4 border-yellow-500 pl-2">
                Connection Preferences
              </h3>
              <p>
                <strong>Purpose:</strong> {user?.meeting_purpose || "N/A"}
              </p>
              <p>
                <strong>Interested In:</strong>{" "}
                {user?.interested_in_gender || "N/A"}
              </p>
            </div>
          )}

          {/* 🟣 Platform Activity */}
          {activeTab === "Platform Activity" && (
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl space-y-2">
              <h3 className="text-base font-semibold text-purple-700 border-l-4 border-purple-500 pl-2">
                Platform & Activity
              </h3>

              <p>
                <strong>Logged In:</strong> {user?.is_logged_in ? "Yes" : "No"}
              </p>
              <p>
                <strong>Login Source:</strong> {user?.login_source || "N/A"}
              </p>
              <p>
                <strong>Last Visited:</strong>{" "}
                {formatDateTimeAmORPm(user?.last_visited) || "N/A"}
              </p>
              <p>
                <strong>Created At:</strong>{" "}
                {formatDateTimeAmORPm(user?.created_at)}
              </p>
              <p>
                <strong>Updated At:</strong>{" "}
                {formatDateTimeAmORPm(user?.updated_at)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
